import { useEffect, useState } from 'react';
import type { ComboOption, ComboboxProps } from './combobox';

export function checkedOptions(value: readonly ComboOption[]): ComboOption[] {
  if (!Array.isArray(value) || value.length > 250) throw new TypeError('combobox_options');
  const ids = new Set<string>();
  return value.map((option) => {
    if (!option || typeof option.id !== 'string' || typeof option.label !== 'string')
      throw new TypeError('combobox_option');
    if (!option.id || option.id.length > 200 || option.label.length > 500 || ids.has(option.id))
      throw new TypeError('combobox_option');
    ids.add(option.id);
    return { id: option.id, label: option.label, disabled: option.disabled === true };
  });
}

// The consumer owns admitted reads. This hook only fences presentation work;
// closing/reopening or replacing the callback never revives old option rows.
export function useComboOptions(props: ComboboxProps, query: string, open: boolean, epoch: number) {
  const load = props.loadOptions;
  const blocked = props.disabled || props.readOnly;
  const [result, setResult] = useState<{
    query: string;
    epoch: number;
    load: typeof load;
    status: 'ready' | 'loading' | 'failed';
    options: ComboOption[];
  }>({ query: '', epoch: -1, load, status: 'loading', options: [] });
  useEffect(() => {
    if (!load || !open || blocked) return;
    const controller = new AbortController();
    let current = true;
    setResult({ query, epoch, load, status: 'loading', options: [] });
    Promise.resolve()
      .then(() => {
        if (!current) return [];
        return load(query, { signal: controller.signal });
      })
      .then(checkedOptions)
      .then((options) => {
        if (current) setResult({ query, epoch, load, status: 'ready', options });
      })
      .catch(() => {
        if (current) setResult({ query, epoch, load, status: 'failed', options: [] });
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [load, query, epoch, open, blocked]);
  if (!open || blocked) return { status: 'ready', options: [] } as const;
  if (!load)
    return {
      status: 'ready',
      options: checkedOptions(props.options ?? []).filter((option) =>
        option.label
          .toLocaleLowerCase(props.locale)
          .includes(query.toLocaleLowerCase(props.locale)),
      ),
    } as const;
  if (result.query !== query || result.load !== load || result.epoch !== epoch)
    return { status: 'loading', options: [] } as const;
  return result;
}
